import { EditorView, keymap } from "@codemirror/view";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { slugify } from "../render/markdown.ts";
import { createId } from "../model/types.ts";
import { suggestImageName } from "../vault/attachments.ts";
import { openWidthDialog } from "../app/dialogs.ts";
import {
  HIGHLIGHT_COLORS,
  type Highlight,
  type HighlightColor,
} from "../model/highlights.ts";
import {
  highlightDecorations,
  highlightsField,
  highlightsOf,
  markingField,
  placeHighlights,
  placedHighlights,
  type PlacedHighlight,
  setHighlights,
  setMarking,
  toHighlights,
} from "./docHighlights.ts";
import { docTitle } from "./docTitle.ts";
import { mdTables } from "./tableWidget.ts";
import { linkAt, livePreview } from "./livePreview.ts";
import { linkCommand, linkHover, type LinkUIOptions } from "./linkUI.ts";
import { formattingKeymap } from "./formatting.ts";
import { MarkerBar, type MarkerTool } from "./markerBar.ts";
import { DocToolbar } from "./DocToolbar.ts";
import { completions } from "./completions.ts";
import { confirmDialog } from "../app/dialogs.ts";
import { intendedPath, normalizeLinkDestination, splitHash } from "../vault/links.ts";

// Mismo peso de trazo que los íconos del toolbar de canvas (1.7/round), para
// que las dos barras flotantes se sientan parte del mismo sistema.
const RAW_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="m8 8-4 4 4 4"/><path d="m16 8 4 4-4 4"/><path d="M14 4.5 10 19.5"/></svg>';

export interface MarkdownViewOptions {
  /** Display name (basename) shown in the toolbar. */
  title: string;
  /** Vault path of this document (used to resolve relative image paths). */
  docPath: string;
  /** Initial document contents. */
  initial: string;
  /**
   * Called (debounced) with the latest contents whenever the user edits.
   * Devolver la promesa de escritura es opcional pero deja que `flush()` la
   * espere, que es lo que necesita cualquiera que vaya a LEER el vault justo
   * después (por ejemplo el barrido de enlaces al renombrar).
   */
  onSave: (contents: string) => void | Promise<void>;
  /** Resaltados guardados de este documento (archivo lateral del vault). */
  highlights: Highlight[];
  /** Called (debounced) con los resaltados a persistir. */
  onSaveHighlights: (highlights: Highlight[]) => void;
  /** Save a pasted/dropped image into the vault; returns its vault path. */
  saveImage?: (blob: Blob, suggestedName: string) => Promise<string>;
  /** Resolve an image `src` (relative to the doc) to a renderable URL, or
   *  null when the file can't be found. External URLs are passed through. */
  resolveImage?: (src: string) => Promise<string | null>;
  /**
   * Ruta del vault a la que apunta un enlace del documento, o null si no
   * existe. Es SÍNCRONA a propósito: se contesta contra el árbol en memoria
   * (ver `vault/links.ts`), así un documento con cien enlaces no cuesta cien
   * lecturas de disco y los rotos se pueden pintar en el mismo render.
   */
  resolveLink?: (href: string) => string | null;
  /**
   * Abrir otro documento del vault (click en un enlace interno).
   *
   * `peek` pide el panel lateral: es el ⌘/Ctrl+click de la hoja. Sin él el
   * enlace se sigue donde está el documento que lo contiene.
   */
  onNavigate?: (path: string, hash: string, opts: { peek: boolean }) => void;
  /** Crear el documento que un enlace roto nombraba; devuelve la ruta creada. */
  onCreateNote?: (path: string) => Promise<string | null>;
  /** Rutas enlazables del vault, para el buscador de `[[`. */
  linkTargets?: () => string[];
}

const SAVE_DEBOUNCE_MS = 400;

/** Escapa una ruta del vault para usarla como destino de un enlace markdown. */
function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

/**
 * Un documento markdown en UNA SOLA HOJA: se lee y se escribe en el mismo
 * lugar (Live Preview, estilo Obsidian/Notion), sin pasar por una vista previa
 * aparte. El marcado se oculta y el texto se ve como va a verse, salvo en la
 * línea donde está el cursor, que muestra el crudo para poder editarlo.
 *
 * El segundo modo —"crudo"— muestra el archivo tal cual: el texto literal que
 * vería cualquier editor de `.txt`, sin decoraciones, sin widgets y sin
 * resaltados. Es el mismo `EditorState` con otras extensiones, así que ir y
 * volver no pierde cursor, scroll ni historial de undo.
 *
 * El `.md` no se toca nunca por presentación: todo lo que se ve son
 * decoraciones de CodeMirror. Editarlo acá y abrirlo en otra app da el mismo
 * archivo, byte por byte.
 *
 * Self-contained: builds its own DOM into the given host and tears it down on
 * `dispose()`, so the Workspace can freely swap it for a CanvasView.
 */
export class MarkdownView {
  private root: HTMLElement;
  private sourceEl: HTMLElement;
  private toolbar: DocToolbar;
  private rawBtn: HTMLButtonElement;
  private marker: MarkerBar;
  private editor: EditorView;
  private saveTimer: number | null = null;
  private hlSaveTimer: number | null = null;
  /**
   * Los resaltados que no se pudieron reubicar al abrir (el documento cambió
   * desde afuera). No se pintan, pero se vuelven a guardar tal cual: perder una
   * marca en silencio sería peor que no poder mostrarla.
   */
  private orphans: Highlight[] = [];
  private mode: "live" | "raw" = "live";
  /** Extensiones que cambian con el modo (decorado) y con el marcador (edición). */
  private modeConf = new Compartment();
  private editableConf = new Compartment();
  /**
   * Las tablas dibujadas. Una sola instancia por vista (ver `mdTables`), y con
   * las mismas opciones de enlace que la hoja: un enlace adentro de una celda
   * se ve y se toca igual que uno de un párrafo.
   */
  private tables = mdTables({
    targets: () => this.opts.linkTargets?.() ?? [],
    docPath: () => this.opts.docPath,
    resolveLink: (href) => this.opts.resolveLink?.(href) ?? null,
    open: (href) => this.openHref(href),
  });

  constructor(private host: HTMLElement, private opts: MarkdownViewOptions) {
    this.root = document.createElement("div");
    this.root.className = "md-view";

    // Un solo interruptor: la hoja normal, o el texto crudo. No hay "vista
    // previa" que elegir porque la hoja normal YA es la vista.
    this.rawBtn = document.createElement("button");
    this.rawBtn.className = "doc-tool";
    this.rawBtn.innerHTML = RAW_ICON_SVG;
    this.rawBtn.title = "Raw text (⌘⇧E)";
    this.rawBtn.setAttribute("aria-label", "Raw text");
    this.rawBtn.addEventListener("click", () =>
      this.setMode(this.mode === "raw" ? "live" : "raw"),
    );

    // Marcador: elegir un color lo deja en la mano hasta soltarlo. Se marca
    // leyendo, y leer es la misma hoja donde se escribe: mientras hay un fibrón
    // en la mano el editor deja de aceptar tecleo y el marcado crudo no vuelve
    // aunque la selección toque la línea.
    // En la barra el marcador son dos botones —fibrón y goma— y los colores
    // viven en la fila de opciones, arriba: no estiran la fila de controles,
    // que es la que se usa todo el tiempo, pero mientras marcás quedan a la
    // vista y no tapan el texto.
    this.marker = new MarkerBar((tool) => this.applyMarker(tool));

    this.toolbar = new DocToolbar();
    this.toolbar.setOptions(this.marker.options);
    this.toolbar.setStart(this.rawBtn);
    this.toolbar.setEnd(this.marker.root);

    const body = document.createElement("div");
    body.className = "md-body";
    this.sourceEl = document.createElement("div");
    this.sourceEl.className = "md-source";
    body.append(this.sourceEl);

    this.root.append(this.toolbar.root, body);
    this.host.appendChild(this.root);

    this.editor = new EditorView({
      parent: this.sourceEl,
      state: this.makeState(opts.initial),
    });

    // Con el marcador en la mano, cada selección hecha con el mouse se pinta al
    // soltar el botón. El timeout deja que el navegador termine de asentar la
    // selección antes de medirla.
    this.sourceEl.addEventListener("mouseup", this.onMouseUp);

    this.root.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.releaseMarker();
        return;
      }
      // Atajos de color: sirven sin tomar el marcador, para una marca suelta
      // mientras se lee. En crudo no: ahí la hoja es el archivo y nada más.
      if (this.mode !== "live" || !(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > HIGHLIGHT_COLORS.length) return;
      e.preventDefault();
      this.markSelection(HIGHLIGHT_COLORS[n - 1]);
    });

    // Paste/drop de imágenes desde el sistema (Finder/Explorer): se guardan en
    // `attachments/` como en el canvas y se inserta su enlace markdown. Se
    // escucha sobre el editor para no robarle el drop a la sidebar ni al canvas.
    this.sourceEl.addEventListener("paste", this.onPaste);
    this.sourceEl.addEventListener("drop", this.onDrop);
    this.sourceEl.addEventListener("dragover", this.onDragOver);

    this.syncModeUI();
  }

  private onPaste = (e: ClipboardEvent): void => {
    const items = e.clipboardData?.items;
    const files: File[] = [];
    if (items) {
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
    }
    if (files.length === 0 || !this.opts.saveImage) return;
    e.preventDefault();
    void this.insertImages(files, this.editor.state.selection.main.head);
  };

  private onDragOver = (e: DragEvent): void => {
    if (!this.opts.saveImage || !e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  private onDrop = (e: DragEvent): void => {
    if (!this.opts.saveImage) return;
    const files = [...(e.dataTransfer?.files ?? [])].filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    e.preventDefault();
    const pos = this.editor.posAtCoords({ x: e.clientX, y: e.clientY });
    void this.insertImages(files, pos ?? this.editor.state.selection.main.head);
  };

  private onMouseUp = (): void => {
    const tool = this.marker.tool;
    if (!tool) return;
    window.setTimeout(() => {
      if (tool === "eraser") this.eraseSelection();
      else this.markSelection(tool);
    }, 0);
  };

  /** Guarda cada imagen en el vault e inserta su enlace `![alt](path)`. */
  private async insertImages(files: File[], at: number): Promise<void> {
    const saveImage = this.opts.saveImage;
    if (!saveImage) return;
    const docDir = this.opts.docPath.slice(0, this.opts.docPath.lastIndexOf("/") + 1);
    const up = docDir ? "../".repeat(docDir.split("/").filter(Boolean).length) : "";
    const inserts: string[] = [];
    for (const f of files) {
      try {
        const path = await saveImage(f, suggestImageName(f.type));
        const base = path.split("/").pop()!;
        const alt = base.replace(/\.[^.]+$/, "");
        // El destino se escribe escapado (`Pasted%20image%20…png`): en markdown
        // un destino sin `<>` no puede llevar espacios, y los nombres de las
        // imágenes pegadas los tienen. Sin esto el `![...]` no era ni un enlace.
        inserts.push(`![${alt}](${encodePath(`${up}attachments/${base}`)})`);
      } catch (err) {
        console.error("insert image failed", err);
      }
    }
    if (inserts.length === 0) return;
    const text = `\n${inserts.join("\n\n")}\n`;
    this.editor.dispatch({
      changes: { from: at, insert: text },
      selection: { anchor: at + text.length },
    });
    this.editor.focus();
  }

  // ── Resaltados ─────────────────────────────────────────────────────────────

  /**
   * Los offsets del documento que abarca la selección del navegador.
   *
   * Se lee la selección del DOM y no `state.selection` porque mientras hay un
   * fibrón en la mano el editor no es editable, y en ese estado CodeMirror no
   * sincroniza la selección: la única verdad está en el DOM.
   */
  private selectionRange(): { from: number; to: number } | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    if (!this.editor.contentDOM.contains(range.commonAncestorContainer)) return null;
    try {
      const a = this.editor.posAtDOM(range.startContainer, range.startOffset);
      const b = this.editor.posAtDOM(range.endContainer, range.endOffset);
      const from = Math.min(a, b);
      const to = Math.max(a, b);
      return to > from ? { from, to } : null;
    } catch {
      return null;
    }
  }

  /**
   * Marca la selección.
   *
   * Los colores SE SUPERPONEN: pasar un fibrón sobre algo ya marcado con otro
   * color no lo borra, se apila encima. Es el gesto del papel — se subraya en
   * amarillo mientras se lee y después se vuelve en rojo sobre lo que quedó
   * dudoso, sin perder la primera pasada.
   *
   * El mismo color, en cambio, no se apila: dos pasadas iguales darían lo mismo
   * sin querer decir nada. Ahí se funden en una sola marca — y si la selección
   * cae entera dentro de una del mismo color, la borra: mismo gesto para marcar
   * y desmarcar, sin goma aparte.
   */
  private markSelection(color: HighlightColor): void {
    const sel = this.selectionRange();
    if (!sel) return;

    const placed = placedHighlights(this.editor.state);
    // Sólo se tocan las marcas de este color; las de los otros quedan debajo.
    // Se cuentan también las que apenas se rozan, para que dos pasadas
    // consecutivas del mismo fibrón queden como una sola marca continua.
    const same = placed.filter(
      (p) => p.color === color && p.from <= sel.to && p.to >= sel.from,
    );
    const covering = same.find((p) => p.from <= sel.from && p.to >= sel.to);

    let next: PlacedHighlight[];
    if (covering) {
      next = placed.filter((p) => p !== covering);
    } else {
      const merged = new Set(same);
      next = [
        ...placed.filter((p) => !merged.has(p)),
        {
          id: createId("hl"),
          color,
          createdAt: Date.now(),
          from: Math.min(sel.from, ...same.map((p) => p.from)),
          to: Math.max(sel.to, ...same.map((p) => p.to)),
        },
      ];
    }
    this.applyHighlights(next);
  }

  /**
   * Borra la marca de más arriba de las que toca la selección.
   *
   * Una sola por pasada, la ÚLTIMA que se puso: con los colores apilados, lo
   * que se ve arriba es lo último marcado, así que borrar de a una deshace las
   * pasadas en el orden inverso al que se hicieron. Borrar todas de un saque
   * sería más rápido pero se llevaría puesto lo que estaba debajo sin que se
   * viera venir, y no hay forma de recuperarlo salvo volver a marcar.
   */
  private eraseSelection(): void {
    const sel = this.selectionRange();
    if (!sel) return;
    const placed = placedHighlights(this.editor.state);
    // "La de arriba" es la más nueva: los rangos vienen en orden del documento,
    // así que quién tapa a quién lo dice la fecha, no la posición.
    let top: PlacedHighlight | null = null;
    for (const p of placed) {
      if (p.from >= sel.to || p.to <= sel.from) continue;
      if (!top || p.createdAt >= top.createdAt) top = p;
    }
    if (!top) return;
    this.applyHighlights(placed.filter((p) => p !== top));
  }

  private applyHighlights(next: readonly PlacedHighlight[]): void {
    this.editor.dispatch({ effects: setHighlights.of(next) });
    // La selección azul taparía el color recién puesto.
    window.getSelection()?.removeAllRanges();
    this.scheduleHighlightSave();
  }

  /**
   * Escape suelta el marcador desde donde sea.
   *
   * Va en el documento y no en la vista porque con el fibrón en la mano el
   * editor no es editable y el foco puede no estar adentro: un modo que se
   * toma con un clic tiene que poder soltarse con una tecla, siempre.
   */
  private onEscape = (e: KeyboardEvent): void => {
    if (e.key === "Escape") this.releaseMarker();
  };

  /** Toma o suelta el marcador: el estado del editor tiene que enterarse. */
  private applyMarker(tool: MarkerTool | null): void {
    // Se marca sobre la hoja, no sobre el crudo: ahí no hay nada que resaltar.
    if (tool && this.mode === "raw") this.setMode("live");
    this.editor.dispatch({
      effects: [
        setMarking.of(tool !== null),
        // Mientras se lee con el fibrón en la mano no se teclea: una tecla
        // suelta escribiría en el documento en vez de marcar.
        this.editableConf.reconfigure(tool ? EditorView.editable.of(false) : []),
      ],
    });
    this.root.classList.toggle("is-marking", tool !== null);
    this.root.classList.toggle("is-erasing", tool === "eraser");
    document.removeEventListener("keydown", this.onEscape);
    if (tool) document.addEventListener("keydown", this.onEscape);
  }

  /** Suelta el marcador (Escape, o al pasar a crudo). */
  private releaseMarker(): void {
    if (!this.marker.tool) return;
    this.marker.setActive(null);
    this.applyMarker(null);
  }

  // ── Navegación ─────────────────────────────────────────────────────────────

  /**
   * Sigue un enlace a otro documento del vault. Si el destino no existe,
   * ofrece crearlo ahí mismo —el gesto de Obsidian— en vez de dejar un enlace
   * muerto: es lo que hace que escribir primero y crear después funcione.
   */
  private async followLink(href: string, peek: boolean): Promise<void> {
    const { hash } = splitHash(href);
    const target = this.opts.resolveLink?.(href) ?? null;
    if (target) {
      this.opts.onNavigate?.(target, hash, { peek });
      return;
    }
    const create = this.opts.onCreateNote;
    const intended = intendedPath(this.opts.docPath, href);
    if (!create || !intended) return;
    const name = intended.split("/").pop() ?? intended;
    const ok = await confirmDialog({
      title: `Create "${name}"?`,
      message: `This link points to "${intended}", which doesn't exist yet.`,
      okLabel: "Create",
    });
    if (!ok) return;
    const created = await create(intended);
    // Una nota recién creada se abre en grande aunque el click pidiera el
    // costado: se la va a escribir, no a consultar.
    if (created) this.opts.onNavigate?.(created, hash, { peek: false });
  }

  /**
   * Lleva la hoja al encabezado que nombra un ancla (`#seccion`).
   *
   * El slug se calcula con el mismo `slugify` que usa el renderizador al
   * escribir los ids del HTML, incluido el sufijo de los repetidos, así un
   * enlace lleva al mismo lado se lo siga desde donde se lo siga.
   */
  revealAnchor(hash: string): void {
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) return;
    const doc = this.editor.state.doc;
    const used = new Map<string, number>();
    for (let i = 1; i <= doc.lines; i++) {
      const line = doc.line(i);
      const m = /^#{1,6}\s+(.*)$/.exec(line.text);
      if (!m) continue;
      const base = slugify(m[1]);
      if (!base) continue;
      const seen = used.get(base) ?? 0;
      used.set(base, seen + 1);
      if ((seen === 0 ? base : `${base}-${seen}`) !== id) continue;
      this.editor.dispatch({
        selection: { anchor: line.from },
        effects: EditorView.scrollIntoView(line.from, { y: "start" }),
      });
      return;
    }
  }

  /** Popup de ancho y reescritura del markdown como `![alt|300](src)`. */
  private async resizeImage(img: HTMLImageElement): Promise<void> {
    const src = img.dataset.src;
    if (!src) return;
    const span = this.findImageSpan(src);
    if (!span) return;
    const current = Number(img.getAttribute("width")) || null;
    const width = await openWidthDialog(current);
    if (width === undefined) return; // cancelado

    const alt = img.getAttribute("alt") ?? "";
    const suffix = width === null ? "" : `|${width}`;
    this.editor.dispatch({
      changes: { from: span.from, to: span.to, insert: `![${alt}${suffix}](${src})` },
    });
  }

  /** Markdown span `![alt](src)` whose src matches, or null. */
  private findImageSpan(src: string): { from: number; to: number } | null {
    const doc = this.editor.state.doc.toString();
    const re = /!\[[^\]]*\]\(([^)\s]*)\)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(doc))) {
      if (m[1] === src) return { from: m.index, to: re.lastIndex };
    }
    return null;
  }

  // ── Estado y modos ─────────────────────────────────────────────────────────

  /**
   * Todo lo que las piezas de enlace necesitan del documento.
   *
   * Lo comparten la ficha del hover, el comando `/link` y el buscador de `[[`:
   * los tres tocan el mismo markdown y tienen que contestar igual sobre qué
   * existe en el vault y a dónde lleva cada destino.
   */
  private linkOptions(): LinkUIOptions {
    return {
      targets: () => this.opts.linkTargets?.() ?? [],
      docPath: () => this.opts.docPath,
      resolveLink: (href) => this.opts.resolveLink?.(href) ?? null,
      open: (href) => this.openHref(href),
    };
  }

  /**
   * Abrir un destino: afuera, a un ancla de esta nota, o a otro documento.
   *
   * `peek` sólo tiene sentido para un documento del vault; un enlace externo se
   * va al navegador y un ancla se resuelve en esta misma hoja.
   */
  private openHref(href: string, { peek = false } = {}): void {
    const destination = normalizeLinkDestination(href, this.opts.linkTargets?.() ?? []);
    if (/^(?:https?:|mailto:|\/\/)/i.test(destination)) {
      window.open(destination, "_blank", "noopener,noreferrer");
    } else if (destination.startsWith("#")) {
      this.revealAnchor(destination);
    } else {
      void this.followLink(destination, peek);
    }
  }

  /** Lo que cambia entre la hoja y el crudo: todo el decorado, y nada más. */
  private modeExtensions(): Extension {
    if (this.mode === "raw") return [];
    return [
      // El resolver se lee de `this.opts` en cada llamada (y no se captura el
      // valor de ahora) para que siga apuntando al documento vigente después
      // de un `load()`.
      livePreview({
        resolveImage: (src) => this.opts.resolveImage?.(src) ?? Promise.resolve(null),
        resolveLink: (href) => this.opts.resolveLink?.(href) ?? null,
      }),
      this.tables,
      linkHover(this.linkOptions()),
      highlightDecorations,
      docTitle(this.opts.title),
    ];
  }

  private makeState(doc: string): EditorState {
    const { placed, orphans } = placeHighlights(doc, this.opts.highlights);
    this.orphans = orphans;
    return EditorState.create({
      doc,
      extensions: [
        history(),
        keymap.of([
          // Antes que el de formato: el enlace se crea por su UI, no escribiendo
          // corchetes a mano. (⌘K igual lo intercepta la búsqueda global de
          // `main.ts`; el camino de todos los días es `/link`.)
          { key: "Mod-k", run: linkCommand(this.linkOptions()), preventDefault: true },
          ...formattingKeymap,
          {
            key: "Mod-Shift-e",
            run: () => {
              this.setMode(this.mode === "raw" ? "live" : "raw");
              return true;
            },
            preventDefault: true,
          },
          ...historyKeymap,
          ...defaultKeymap,
        ]),
        // Con GFM: sin esto el parser es CommonMark puro y no existen ni el
        // tachado ni las tablas, que el resto de la app sí renderiza.
        markdown({ base: markdownLanguage }),
        markingField,
        highlightsField.init(() => highlightsOf(placed)),
        // Igual que el resolver de imágenes: las opciones se leen de
        // `this.opts` en cada llamada para que sigan valiendo tras un `load()`.
        completions(this.linkOptions()),
        this.modeConf.of(this.modeExtensions()),
        this.editableConf.of([]),
        EditorView.domEventHandlers({
          // En la hoja, clickear un enlace NAVEGA —sin modificadores—. Se
          // puede porque ahí un enlace es una pieza: su markdown no se revela
          // ni se edita a mano (se toca desde su ficha), así que el click no le
          // está robando el gesto a nadie. En crudo, donde todo es texto que se
          // escribe, hace falta ⌘.
          //
          // Se usa `mousedown` y no `click` para ganarle a la colocación del
          // cursor de CodeMirror.
          mousedown: (e, view) => {
            if (e.button !== 0) return false;
            const mod = e.metaKey || e.ctrlKey;
            if (this.mode === "raw" && !mod) return false;
            const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos === null) return false;
            const link = linkAt(view.state, pos);
            if (!link || pos < link.from || pos > link.to) return false;
            e.preventDefault();
            // En la hoja, ⌘ abre al costado. En crudo ⌘ ya está tomado: es lo
            // que distingue "seguir el enlace" de "poner el cursor", así que no
            // queda modificador libre y ahí el enlace abre siempre en grande.
            this.openHref(link.href, { peek: this.mode !== "raw" && mod });
            return true;
          },
          // Click en una imagen: popup para ajustar su ancho de visualización,
          // que se escribe de vuelta al markdown como `![alt|300](src)`.
          click: (e) => {
            const img = (e.target as HTMLElement | null)?.closest?.("img.cm-lp-img");
            if (!(img instanceof HTMLImageElement)) return false;
            if (this.marker.tool) return false; // marcando o borrando, no ajustando
            e.preventDefault();
            void this.resizeImage(img);
            return true;
          },
        }),
        EditorView.lineWrapping,
        EditorView.theme({
          "&": { height: "100%", fontSize: "14px" },
          ".cm-content": {
            padding: "16px 18px var(--md-toolbar-space)",
            maxWidth: "820px",
            margin: "0 auto",
          },
        }),
        EditorView.updateListener.of((u) => {
          if (!u.docChanged) return;
          this.scheduleSave();
          // Los resaltados se movieron con el texto (CodeMirror mapeó sus
          // rangos): sus anclas ya no dicen lo mismo y hay que reescribirlas.
          // En un documento sin ninguna marca no hay nada que reescribir, y
          // guardar de todos modos costaría un barrido del vault por tecla
          // (ver `writeHighlights`, que borra el lateral vacío).
          if (u.state.field(highlightsField).size > 0 || this.orphans.length > 0) {
            this.scheduleHighlightSave();
          }
        }),
      ],
    });
  }

  private setMode(mode: "live" | "raw"): void {
    if (mode === this.mode) return;
    this.mode = mode;
    // En crudo se ve el archivo, no una lectura del archivo: el marcador no
    // tiene dónde pintar.
    if (mode === "raw") this.releaseMarker();
    this.editor.dispatch({ effects: this.modeConf.reconfigure(this.modeExtensions()) });
    this.syncModeUI();
    this.editor.focus();
  }

  private syncModeUI(): void {
    const raw = this.mode === "raw";
    this.rawBtn.classList.toggle("active", raw);
    this.rawBtn.setAttribute("aria-pressed", String(raw));
    this.sourceEl.classList.toggle("is-raw", raw);
  }

  /**
   * Swap in another markdown document, reusing this view's DOM, editor and
   * toolbar. The Workspace uses this when going from one .md to another so
   * nothing in the shell unmounts — a full teardown/remount is what made the
   * UI flash on every file change.
   */
  load(
    opts: Pick<
      MarkdownViewOptions,
      | "title"
      | "docPath"
      | "initial"
      | "onSave"
      | "highlights"
      | "onSaveHighlights"
      | "saveImage"
      | "resolveImage"
      | "resolveLink"
      | "onNavigate"
      | "onCreateNote"
      | "linkTargets"
    >,
  ): void {
    void this.flush(); // save the outgoing document with its own onSave
    this.opts = { ...this.opts, ...opts };
    this.releaseMarker();
    // El scroll vuelve arriba ANTES de instalar el documento nuevo. CodeMirror
    // arma su ventana visible contra el desplazamiento que encuentra al
    // construirse, y reemplazar el estado no lo devuelve a cero: viniendo de
    // una nota leída hasta el final, montaba un tramo del medio del documento
    // nuevo para descartarlo enseguida.
    this.editor.scrollDOM.scrollTop = 0;
    // A fresh state (rather than a replace transaction) also resets the undo
    // history, so undo can't reach back into the previous document.
    this.editor.setState(this.makeState(opts.initial));
    this.syncModeUI(); // el modo elegido se conserva entre documentos
  }

  /** Dónde está parada la lectura ahora mismo, para poder volver a este punto. */
  get scrollTop(): number {
    return this.editor.scrollDOM.scrollTop;
  }

  /**
   * Devuelve la hoja a un punto guardado.
   *
   * Va en el cuadro siguiente a propósito: `load()` pone el desplazamiento en
   * cero antes de instalar el documento nuevo, y CodeMirror arma su ventana
   * visible recién al medirse. Pedirle el desplazamiento en el mismo cuadro en
   * que se montó lo deja arriba de todo igual, porque todavía no sabe cuánto
   * mide lo que tiene adentro.
   */
  scrollTo(top: number): void {
    requestAnimationFrame(() => {
      this.editor.scrollDOM.scrollTop = top;
    });
  }

  private scheduleSave(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => {
      this.opts.onSave(this.editor.state.doc.toString());
      this.saveTimer = null;
    }, SAVE_DEBOUNCE_MS);
  }

  private scheduleHighlightSave(): void {
    if (this.hlSaveTimer !== null) clearTimeout(this.hlSaveTimer);
    this.hlSaveTimer = window.setTimeout(() => {
      this.hlSaveTimer = null;
      this.saveHighlights();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Se guardan todos, incluidos los huérfanos: no se pintan, pero no se pierden. */
  private saveHighlights(): void {
    this.opts.onSaveHighlights(toHighlights(this.editor.state, this.orphans));
  }

  /**
   * Flush any pending debounced save immediately (e.g. before switching docs).
   *
   * La promesa se resuelve cuando la escritura terminó de verdad. Quien sólo
   * quiera soltar lo pendiente puede ignorarla —los errores se registran acá
   * adentro, así que nunca queda un rechazo sin atender.
   */
  flush(): Promise<void> {
    const pending: (void | Promise<void>)[] = [];
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      pending.push(this.opts.onSave(this.editor.state.doc.toString()));
    }
    if (this.hlSaveTimer !== null) {
      clearTimeout(this.hlSaveTimer);
      this.hlSaveTimer = null;
      this.saveHighlights();
    }
    return Promise.all(pending).then(
      () => undefined,
      (err: unknown) => {
        console.error("save failed", err);
      },
    );
  }

  dispose(): void {
    void this.flush();
    document.removeEventListener("keydown", this.onEscape);
    this.editor.destroy();
    this.sourceEl.removeEventListener("mouseup", this.onMouseUp);
    this.sourceEl.removeEventListener("paste", this.onPaste);
    this.sourceEl.removeEventListener("drop", this.onDrop);
    this.sourceEl.removeEventListener("dragover", this.onDragOver);
    // La barra vive dentro de la vista, así que se va con ella.
    this.root.remove();
  }
}
